import { useState } from "react";
import { Download, Package, Repeat } from "lucide-react";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { ListCapNotice } from "../components/ui/ListCapNotice";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet, downloadBlob, PortalApiError } from "../api/portal-client";
import { usePortalResource } from "../hooks/usePortalResource";

interface Produit {
  id: string;
  nom: string;
  marque?: string | null;
  imageUrl?: string | null;
  nutriscoreGrade?: string | null;
  hasFichePubliee: boolean;
  isCadencier: boolean;
}

interface ProduitsResponse {
  produits: Produit[];
}

/** Plafond serveur de `/me/produits` (portal-auth.routes.ts). */
const PRODUITS_CAP = 300;

// Couleurs officielles Nutri-Score (A-E). Texte clair ou sombre selon la lisibilité.
const NUTRISCORE: Record<string, { bg: string; fg: string }> = {
  A: { bg: "#038141", fg: "#FFFFFF" },
  B: { bg: "#85BB2F", fg: "#1A1A1A" },
  C: { bg: "#FECB02", fg: "#1A1A1A" },
  D: { bg: "#EE8100", fg: "#FFFFFF" },
  E: { bg: "#E63E11", fg: "#FFFFFF" },
};

function NutriScoreBadge({ grade }: { grade: string }) {
  const g = grade.trim().toUpperCase();
  const colors = NUTRISCORE[g];
  if (!colors) return null;
  return (
    <span
      className="inline-flex items-center justify-center w-7 h-7 rounded-md text-sm font-bold shrink-0"
      style={{ backgroundColor: colors.bg, color: colors.fg }}
      aria-label={`Nutri-Score ${g}`}
      title={`Nutri-Score ${g}`}
    >
      {g}
    </span>
  );
}

function ProductImage({ src, alt }: { src?: string | null; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="w-full aspect-[4/3] bg-cream flex items-center justify-center text-muted rounded-t-xl2">
        <Package size={40} aria-hidden />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className="w-full aspect-[4/3] object-cover rounded-t-xl2 bg-cream"
    />
  );
}

/**
 * Fiches produits.
 *
 * W7-B — la carte « aucune fiche disponible » s'affichait aussi quand la lecture
 * avait échoué : un client hors-ligne concluait que son fournisseur ne publiait
 * rien. L'état est nommé, le vide porte une sortie, et le plafond de 300
 * produits est dit.
 */
export function ProductSheetsPage() {
  const { data, status, error, reload } = usePortalResource<ProduitsResponse>(() =>
    apiGet<ProduitsResponse>("/me/produits"),
  );
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const list = data?.produits ?? [];

  async function handleDownload(p: Produit) {
    setDownloadError(null);
    setDownloadingId(p.id);
    try {
      const safeName = p.nom.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "").toLowerCase();
      await downloadBlob(`/me/produits/${p.id}/fiche.pdf`, `fiche-${safeName || p.id.slice(-8)}.pdf`);
    } catch (e: unknown) {
      setDownloadError(
        e instanceof PortalApiError && e.status === 404
          ? "Aucune fiche publiée pour ce produit."
          : e instanceof PortalApiError
            ? e.message
            : "Le téléchargement a échoué.",
      );
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-6">
      <PageHeader
        title="Fiches produits"
        subtitle="Consultez et téléchargez les fiches techniques de vos produits."
      />

      <PortalState status={status} label="vos fiches produits" error={error} onRetry={reload} />

      {downloadError && (
        <p role="alert" className="text-sm text-danger mb-4">
          {downloadError}
        </p>
      )}

      {status === "ready" && list.length === 0 && (
        <EmptyState
          icon={Package}
          title="Aucune fiche produit disponible pour le moment"
          description="Votre fournisseur n'a publié aucune fiche technique sur les produits de votre catalogue."
          action={{ to: "/messages", label: "Demander une fiche" }}
        />
      )}

      {status === "ready" && list.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((p) => (
              <article
                key={p.id}
                className="bg-card border border-border rounded-xl2 shadow-sm overflow-hidden flex flex-col"
              >
                <ProductImage src={p.imageUrl} alt={p.nom} />
                <div className="p-4 flex flex-col gap-2 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-ink text-base leading-tight">{p.nom}</h3>
                      {p.marque && <p className="text-xs text-muted truncate">{p.marque}</p>}
                    </div>
                    {p.nutriscoreGrade && <NutriScoreBadge grade={p.nutriscoreGrade} />}
                  </div>

                  {p.isCadencier && (
                    <span className="inline-flex items-center gap-1 self-start rounded-full bg-terracotta/10 text-terracotta text-xs font-semibold px-2 py-0.5">
                      <Repeat size={12} aria-hidden /> Commandé régulièrement
                    </span>
                  )}

                  <div className="mt-auto pt-2">
                    {p.hasFichePubliee ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full"
                        onClick={() => handleDownload(p)}
                        disabled={downloadingId === p.id}
                      >
                        <Download size={16} />
                        {downloadingId === p.id ? "Téléchargement..." : "Télécharger la fiche produit (PDF)"}
                      </Button>
                    ) : (
                      <p className="text-xs text-muted text-center py-2">Fiche produit non disponible</p>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <ListCapNotice shown={list.length} cap={PRODUITS_CAP} label="produits" genre="m" />
        </>
      )}
    </main>
  );
}
